/* eslint-disable react/prop-types */

import React, { useCallback, useEffect, useState } from "react";
import {
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
} from "@nextui-org/table";
import AppUserProfile, { UserType } from "../../../assets/js/AppUserProfile";
import { downloadPublicBlobFile } from "../../../services/BlobService";
import AppUtility from "../../../assets/js/AppUtility";
import PropTypes from "prop-types";
import {
  stringToTicketStatus,
  TicketPhase,
  TicketStatus,
} from "../../../assets/js/AppTicketConstant";
import DealerActionButtons from "./DealerActionButtons";
import ApproverActionButtons from "./ApproverActionButtons";
import { actionsPropType } from "../../../models/ActionProp";
import useTicketStore from "../../../stores/useTicketStore";
import { useDisclosure } from "@nextui-org/react";
import RejectDescriptionModal from "../../RejectDescriptionModal";
import useSubTicketStore from "../../../stores/useSubTicketStore";
import { getStatusDisplayName } from "../../../assets/js/FilterAppTicketConstant";
import useClaimStore from "../../../stores/useClaimStore";
import Swal from "sweetalert2";

const columnPropType = PropTypes.shape({
  name: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  key: PropTypes.string.isRequired,
  className: PropTypes.string,
  width: PropTypes.number,
});

const dataPropType = PropTypes.shape({
  key: PropTypes.any,
  value: PropTypes.shape({
    key: PropTypes.any,
    value: PropTypes.any,
  }),
  className: PropTypes.string,
});

const SubTicketTable = ({
  columns,
  data,
  actions,
  
  
  ticketPhase,
  ticketStatus,
  ticketSequenceApproved,
  id,
}) => {
  const userRole = AppUserProfile.getData()["whoami"];
  const ticketDetail = useTicketStore((state) => state.getTicketDetailById(id));
  const fetchTicket = useTicketStore((state) => state.fetchTicketById);

  
  const [rejectReason, setRejectReason] = useState(null);
  const [currentSubTicket, setCurrentSubTicket] = useState(null); 

  const { alreadyReviewed } = useSubTicketStore();

  useEffect(() => {
    if (id) {
      fetchTicket(id);
    }
  }, [id, fetchTicket]);

  const getLastestRejectReason = useCallback(({ item }) => {
    
    if (!ticketDetail?.sub_tickets) {
      return false;
    }

    const subTicket = ticketDetail.sub_tickets.find(
      (subTicket) => subTicket.id === item.value["id"].data
    );

    if (!subTicket) {
      return false; 
    }

    const lastReview = subTicket.reviews?.at(-1);
    if (!lastReview) {
      return false; 
    }

    setCurrentSubTicket(subTicket);
    setRejectReason(lastReview);
    return true; 
  }, [ticketDetail]);

  const {
    isOpen: isRejectDescriptionModalOpen,
    onOpenChange: isRejectDescriptionModalOpenChange,
  } = useDisclosure();

  const { getClaimById } = useClaimStore();

  const checkSubTicketAction = useCallback(({ subTiketId }) => {
    const reviewSubTicketStatus = alreadyReviewed({
      subTicketId: subTiketId,
      phase: ticketPhase,
    });
    if (reviewSubTicketStatus == null) return null;

    return stringToTicketStatus(reviewSubTicketStatus) || null;
  }, [alreadyReviewed, ticketPhase]);

  const handleRejectClick = useCallback((item) => {
    const isRejectedStatus = item.value["status"].data === TicketStatus.Rejected.value;
    
    if (isRejectedStatus) {
      const review = getLastestRejectReason({ item });
      if (review) {
        isRejectDescriptionModalOpenChange(true);
      } else {
        Swal.fire({
          html: `<h4>ไม่สามารถโหลดข้อมูลได้ กรุณาลองใหม่อีกครั้ง</h4>`,
          icon: "warning",
          confirmButtonText: "Ok",
        });
      }
    }
  }, [getLastestRejectReason, isRejectDescriptionModalOpenChange]);

  const renderDataCell = useCallback((data, columnKey) => {
    const value = data.value[columnKey].data;
    const className = data.value[columnKey].className ?? "";

    if (columnKey === "id") return null;

    if (columnKey.includes("file")) {
      return (
        <div key={`${id}-files`} className="flex flex-row gap-x-3 gap-y-1 flex-wrap">
          {value.map((url, index) => (
            <div
              key={`${id}-file-${index}`}
              className="p-2 w-24 h-16 rounded-xl shrink border-light-gray-300 border-2 bg-white flex items-center justify-center text-center hover:bg-light-gray-400 cursor-pointer"
              onClick={() => downloadPublicBlobFile(url)}
            >
              <p className="line-clamp-2 break-words text-xs md:text-sm">
                {AppUtility.getFileNameFromUrl(url)}
              </p>
            </div>
          ))}
        </div>
      );
    }

    if (columnKey === "action") {
      return (
        actions && (
          <div className="flex flex-row gap-x-5 items-center">
            {Object.entries(actions).map(
              ([actionType, action]) =>
                action && renderActionCell(data.value, actionType)
            )}
          </div>
        )
      );
    }

    if (columnKey === "status") {
      return buildStatusChip({ status: value });
    }

    return (
      <div key={`${id}-${columnKey}`} className={className}>
        <p className="text-black text-base text-nowrap">
          {columnKey.includes("amount")
            ? Number(value).toLocaleString(undefined, {
                minimumFractionDigits: 0,
                maximumFractionDigits: 5,
              })
            : value}
        </p>
      </div>
    );
  }, [actions, id]);

  const renderActionCell = useCallback(
    (data, actionType) => {
      switch (userRole) {
        case UserType.DEALER_DENZA:
        case UserType.DEALER_BYD: {
          const claimData = getClaimById(data.id.data);
          const reviewAction = checkSubTicketAction({
            subTiketId: data.id.data,
          });

          return DealerActionButtons({
            actions: actions,
            data: data,
            actionType: actionType,
            ticketPhase: ticketPhase,
            ticketStatus: ticketStatus,
            ticketSequenceApproved: ticketSequenceApproved,
            claimData: claimData,
            reviewAction: reviewAction,
          });
        }
        case UserType.REVER:
        case UserType.ACCOUNTANT:
        case UserType.BUSINESS_CONTROL:
          return ApproverActionButtons({
            actions: actions,
            data: data,
            actionType: actionType,
            ticketPhase: ticketPhase,
            ticketStatus: ticketStatus,
            ticketSequenceApproved: ticketSequenceApproved,
          });
        default:
          return null;
      }
    },
    [userRole, actions, getClaimById, checkSubTicketAction, ticketPhase, ticketStatus, ticketSequenceApproved]
  );

  const buildStatusChip = useCallback(({ status }) => {
    let chipColor = "";
    let chipStatus = "";

    const displayStatus = getStatusDisplayName({
      phase: ticketPhase.value,
      status: status,
      approveSequence: ticketSequenceApproved,
    });

    chipStatus = displayStatus.name;
    const ticketStatusEnum = stringToTicketStatus(status);
    
    switch (userRole) {
      case UserType.DEALER_DENZA:
      case UserType.DEALER_BYD:
        switch (ticketStatusEnum) {
          case TicketStatus.AwaitingApproval:
            chipColor = "bg-light-blue";
            break;
          case TicketStatus.Approved:
            chipColor = "bg-complete";
            break;
          case TicketStatus.Rejected:
            chipColor = "bg-error";
            break;
          default:
            chipColor = "bg-gray-400";
            break;
        }
        break;
      case UserType.REVER:
      case UserType.ACCOUNTANT:
      case UserType.BUSINESS_CONTROL:
        switch (ticketStatusEnum) {
          case TicketStatus.AwaitingApproval:
            chipColor = "bg-warning";
            break;
          case TicketStatus.Approved:
            chipColor = "bg-complete";
            break;
          case TicketStatus.Rejected:
            chipColor = "bg-error";
            break;
          default:
            chipColor = "bg-gray-400";
            break;
        }
        break;
      default:
        chipColor = "bg-gray-400";
        break;
    }

    return (
      <div
        key={`${id}-status`}
        className={`${chipColor} rounded-xl px-2 py-1 text-nowrap font-medium text-white text-center`}
      >
        {chipStatus}
      </div>
    );
  }, [userRole, ticketPhase, ticketSequenceApproved, id]);

  
  const handleModalClose = useCallback((isOpen) => {
    if (!isOpen) {
      setCurrentSubTicket(null);
      setRejectReason(null);
    }
    isRejectDescriptionModalOpenChange(isOpen);
  }, [isRejectDescriptionModalOpenChange]);

  return (
    <>
      <Table
        key={id}
        fullWidth
        classNames={{
          thead: "border-0",
          th: "border-0",
          tbody:
            "[&>tr]:border-b [&>tr]:border-black-opacity-40 [&>tr:last-child]:border-0 ",
        }}
      >
        <TableHeader columns={columns}>
          {(column) => (
            <TableColumn
              key={column.key}
              hideHeader={column.key.toLowerCase() === "action"}
              width={column.width}
              className={`text-base text-black bg-[#EAEAEA] py-3 ${
                column.className || ""
              }`}
            >
              {column.name}
            </TableColumn>
          )}
        </TableHeader>
        <TableBody items={data}>
          {(item) => (
            <TableRow key={`${item.key}-${item.value["status"]?.data || 'no-status'}`}>
              {(columnKey) => {
                const reviewAction = checkSubTicketAction({
                  subTiketId: item.value?.["id"]?.data,
                });
   
                const isRejected = stringToTicketStatus(item.value["status"]?.data) === TicketStatus.Rejected;
                const isReviewRejected = reviewAction === TicketStatus.Rejected;
                const isReviewApproved = reviewAction === TicketStatus.Approved;

                return (
                  <TableCell
                    key={`${item.key}-${columnKey}`}
                    onClick={() => handleRejectClick(item)}
                    className={`${
                      item.value[columnKey]?.className ?? ""
                    } overflow-auto ${
                      isRejected || isReviewRejected 
                        ? "bg-red-100 cursor-pointer" 
                        : ""
                    } ${
                      isReviewApproved 
                        ? "bg-complete bg-opacity-25" 
                        : ""
                    }`}
                  >
                    {renderDataCell(item, columnKey)}
                  </TableCell>
                );
              }}
            </TableRow>
          )}
        </TableBody>
      </Table>
      {currentSubTicket && rejectReason && (
        <RejectDescriptionModal
          isOpen={isRejectDescriptionModalOpen}
          onOpenChange={handleModalClose}
          rejectReason={rejectReason}
          subTicketData={currentSubTicket}
          ticketPhase={ticketDetail?.phase}
        />
      )}
    </>
  );
};

SubTicketTable.propTypes = {
  columns: PropTypes.arrayOf(columnPropType).isRequired,
  data: PropTypes.arrayOf(dataPropType).isRequired,
  actions: PropTypes.oneOfType([actionsPropType, PropTypes.oneOf([null])]),
  fileRender: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.oneOf([null]),
  ]),
  imageRender: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.oneOf([null]),
  ]),
  ticketPhase: PropTypes.oneOf(Object.values(TicketPhase)),
  ticketSequenceApproved: PropTypes.any,
  id: PropTypes.any,
  ticketStatus: PropTypes.any,
};

export default SubTicketTable;